// src/components/reels/ReelFeed.js - Simple feed with separate cards
import React, { useState, useEffect } from 'react';
import ReelPlayer from './ReelPlayer.js';
import api from '../../utils/api';

const ReelFeed = ({ feedType = 'reels', reels: propReels, onReaction, onLoadMore, hasMore, loading: propLoading }) => {
  const [localReels, setLocalReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use props if provided, otherwise fetch locally
  const reels = propReels || localReels;
  const isLoading = propLoading !== undefined ? propLoading : loading;

  useEffect(() => {
    if (!propReels) {
      fetchReels();
    }
  }, [feedType, propReels]);

  const fetchReels = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let endpoint = '/api/feeds/reels';
      if (feedType === 'trending') {
        endpoint = '/api/feeds/trending-reels';
      }
      
      const response = await api.get(endpoint);
      setLocalReels(response.data.posts || []);
    } catch (error) {
      console.error('Error fetching reels:', error);
      setError(error.message || `Failed to fetch ${feedType} reels`);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      if (onReaction) {
        await onReaction(postId, 'like');
        return;
      }

      const response = await api.post(`/api/posts/${postId}/react`, {
        reactionType: 'like'
      });

      if (response.status === 200) {
        const updateReels = propReels ? setLocalReels : setLocalReels;
        updateReels(prev => prev.map(reel => 
          (reel.id === postId || reel._id === postId)
            ? { 
                ...reel, 
                isLiked: !reel.isLiked,
                reactions: {
                  ...reel.reactions,
                  likes: reel.isLiked 
                    ? (reel.reactions?.likes || 1) - 1 
                    : (reel.reactions?.likes || 0) + 1
                }
              }
            : reel
        ));
      }
    } catch (error) {
      console.error('Error liking reel:', error);
    }
  };

  const handleView = async (postId) => {
    try {
      await api.post(`/api/posts/${postId}/view`);
      
      const updateReels = propReels ? setLocalReels : setLocalReels;
      updateReels(prev => prev.map(reel => 
        (reel.id === postId || reel._id === postId)
          ? { ...reel, views: (reel.views || 0) + 1 }
          : reel
      ));
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  const handleComment = (postId) => {
    window.location.href = `/post/${postId}`;
  };

  const handleShare = (postId) => {
    const postUrl = `${window.location.origin}/post/${postId}`;
    navigator.clipboard.writeText(postUrl).then(() => {
      alert('Link copied to clipboard!');
    }).catch(() => {
      console.log('Share reel:', postId);
    });
  };

  // Loading state
  if (isLoading && reels.length === 0) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-600">Loading {feedType}...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-center">
          <div className="text-red-500 mb-4">Error: {error}</div>
          <button
            onClick={fetchReels}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (reels.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            No {feedType} available
          </h3>
          <p className="text-gray-500">
            {feedType === 'reels' 
              ? 'Create your first reel!' 
              : 'No trending reels this week'}
          </p>
        </div>
      </div>
    );
  }

  // Main render - Simple list of post cards
  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      {reels.map((reel, index) => {
        if (!reel || typeof reel !== 'object') {
          console.warn('Invalid reel object at index', index, reel);
          return null;
        }

        const reelId = reel.id || reel._id || `reel-${index}`;
        
        return (
          <div key={reelId} className="mb-6">
            <ReelPlayer
              reel={reel}
              onLike={handleLike}
              onComment={handleComment}
              onShare={handleShare}
              onView={handleView}
              isActive={true} // All videos can play since they're separate cards
            />
          </div>
        );
      })}

      {/* Load More Button */}
      {hasMore && onLoadMore && (
        <div className="flex justify-center mt-8">
          <button
            onClick={onLoadMore}
            disabled={isLoading}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
          >
            {isLoading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ReelFeed;